/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React from 'react';

import { jsx } from '@atlaskit/css';
import type { ShortcutsConfig } from '@atlassian/global-side-navigation/types';

import {
	type InvitedAppShortcutAllowedSourceAppKey,
	type InvitedAppShortcutRecommendedAppInput,
} from '../../../common/types';
import { toRecommendedProducts } from '../../../common/utils/invited-app-shortcuts-utils';
import type { UseInvitedAppShortcutsResult } from '../../../services/invited-app-shortcuts';
import { useInvitedAppAnalytics } from '../../../services/invited-app-shortcuts/invited-app-analytics';
import { InvitedAppShortcutsContainerProdInner } from './invited-app-shortcuts-container-prod-inner';
import { InvitedAppCollaboratorsLazyProvider } from './invited-app-shortcuts-container-prod-inner/invited-app-collaborators-lazy-provider';

type ErrorBoundaryProps = {
	children: React.ReactNode;
	fallback: (renderProps: { referrableShortcuts: ShortcutsConfig }) => React.ReactNode;
	fireInvitedAppShortcutsContainerRenderFailed: (
		params: Parameters<
			ReturnType<typeof useInvitedAppAnalytics>['fireInvitedAppShortcutsContainerRenderFailed']
		>[0],
	) => void;
	shortcutsConfig: ShortcutsConfig;
};

// eslint-disable-next-line @repo/internal/react/no-class-components -- using a class component for error boundary
class ErrorBoundary extends React.Component<ErrorBoundaryProps, { hasError: boolean }> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	componentDidCatch(error: Error, _errorInfo: React.ErrorInfo) {
		this.props.fireInvitedAppShortcutsContainerRenderFailed({
			error: error.message,
			errorStack: error.stack,
			targetApps: Object.values(this.props.shortcutsConfig)
				.map((shortcut) => shortcut.analyticsName)
				.filter(Boolean) as string[],
		});
	}

	render() {
		if (this.state.hasError) {
			return this.props.fallback({ referrableShortcuts: this.props.shortcutsConfig });
		}

		return this.props.children;
	}
}

export interface InvitedAppShortcutsContainerProps {
	children: (renderProps: { referrableShortcuts: ShortcutsConfig }) => React.ReactNode;
	cloudId: string;
	/**
	 * The number of shortcuts from the end to exclude from getting refs.
	 * For example, if there are 5 shortcuts and excludeLastFromRefs is 2,
	 * only the first 3 shortcuts will get refs.
	 */
	excludeLastFromRefs?: number;
	hoverDelayTimeMs?: number;
	/**
	 * Accepts either bare product keys or `{ product, variant }` objects; bare
	 * keys normalise to `variant: 'none'`. Must match the value passed to
	 * `useInvitedAppShortcuts`.
	 */
	recommendedApps: ReadonlyArray<InvitedAppShortcutRecommendedAppInput>;
	resolverResult: UseInvitedAppShortcutsResult;
	shortcutsConfig: ShortcutsConfig;
	sourceApp: InvitedAppShortcutAllowedSourceAppKey;
	userId: string;
}

export const InvitedAppShortcutsContainer = (
	props: InvitedAppShortcutsContainerProps,
): JSX.Element => {
	const { fireInvitedAppShortcutsContainerRenderFailed } = useInvitedAppAnalytics();
	const { cloudId, userId, recommendedApps, sourceApp, ...inner } = props;

	return (
		<ErrorBoundary
			fallback={inner.children}
			shortcutsConfig={inner.shortcutsConfig}
			fireInvitedAppShortcutsContainerRenderFailed={fireInvitedAppShortcutsContainerRenderFailed}
		>
			<InvitedAppCollaboratorsLazyProvider
				cloudId={cloudId}
				userId={userId}
				// Collaborator fetching is variant-agnostic, so it keeps taking
				// plain product keys rather than propagating the union.
				eligibleApps={toRecommendedProducts(recommendedApps)}
				sourceApp={sourceApp}
			>
				<InvitedAppShortcutsContainerProdInner {...inner} cloudId={cloudId} sourceApp={sourceApp} />
			</InvitedAppCollaboratorsLazyProvider>
		</ErrorBoundary>
	);
};
